import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, Tooltip, WithStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';

import { PopperSquigglies, PopperSquiggliesProps } from 'deprecated/query-triggers';

import IconSquiggly, { IconSquigglyProps } from 'icons/IconSquiggly';

import Button from './Button';
import IconButton, { IconButtonProps } from './IconButton';
import Typography from './Typography';

const styles = (theme: Theme) =>
  createStyles({
    button: {
      backgroundColor: `${theme.palette.common.white} !important`,
      border: `1px solid ${theme.custom.palette.colorGrayishBlue}`,
      borderRadius: theme.spacing.unit / 2,
      height: 40,
      width: 153,
      whiteSpace: 'nowrap',
    },
    buttonIcon: {
      color: theme.custom.palette.colorSupportSteel,
      fontSize: 17,
    },
    buttonLabel: {
      color: theme.palette.text.primary,
      fontWeight: 500,
      padding: `0px ${theme.spacing.unit}px`,
    },
    popper: {
      [theme.breakpoints.down('xs')]: {
        height: '90%',
        width: '90%',
      },
    },
  });

const messages = defineMessages({
  squigglyBtn: {
    id: 'components.v2.ButtonSquigglies.squigglyBtn',
    defaultMessage: 'Insert Squiggly',
  },
});

export interface ButtonSquiggliesProps extends Omit<IconButtonProps, 'classes' | 'onClick'>, WithStyles<typeof styles> {
  squigglies: PopperSquiggliesProps['squigglies'];
  includePatientFormTemplates?: boolean;
  includeLumabotFlowTemplates?: boolean;
  onSquigglySelected?: (squiggly: any) => void;
  iconProps?: IconSquigglyProps;
  tooltipTitle?: string;
  iconMode?: boolean;
}

const ButtonSquigglies: React.FC<ButtonSquiggliesProps> = ({
  squigglies,
  includePatientFormTemplates,
  includeLumabotFlowTemplates,
  onSquigglySelected,
  iconProps,
  tooltipTitle,
  iconMode = false,
  className,
  classes,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const [anchorEl, setAnchorEl] = useState<any>();

  const handleOpen = useCallback(
    (event: React.MouseEvent) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
  }, [setAnchorEl]);

  const handleSquigglySelected = useCallback(
    (squiggly: any) => {
      handleClose();
      if (onSquigglySelected) {
        onSquigglySelected(squiggly);
      }
    },
    [handleClose, onSquigglySelected],
  );

  const tooltipWrapButton = (baseButton: React.ReactElement) =>
    tooltipTitle ? (
      <Tooltip title={tooltipTitle}>
        <div>{baseButton}</div>
      </Tooltip>
    ) : (
      baseButton
    );

  return (
    <>
      {iconMode ? (
        tooltipWrapButton(
          <IconButton data-lh-id="icon-button-squigglies" onClick={handleOpen} {...rest}>
            <IconSquiggly {...iconProps} />
          </IconButton>,
        )
      ) : (
        <Button
          data-lh-id="button-squigglies"
          onClick={handleOpen}
          variant="outlined"
          type="button"
          aria-label="Insert Squiggly"
          className={classnames(classes.button, className)}
        >
          <IconSquiggly className={classes.buttonIcon} {...iconProps} />
          <Typography variant="inherit" className={classes.buttonLabel}>
            {formatMessage(messages.squigglyBtn)}
          </Typography>
        </Button>
      )}
      <PopperSquigglies
        className={classes.popper}
        squigglies={squigglies}
        anchorEl={anchorEl}
        includePatientFormTemplates={includePatientFormTemplates}
        includeLumabotFlowTemplates={includeLumabotFlowTemplates}
        onSquigglySelected={handleSquigglySelected}
        onClickAway={handleClose}
      />
    </>
  );
};

export default withStyles(styles)(ButtonSquigglies);
